// Full search results page with filters
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronDown, Loader2 } from 'lucide-react';
import { ProductCard } from '../../components/shopper/ProductCard';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { BackButton } from '../../components/ui/BackButton';
import { SHOPPER_HOME } from '../../constants/routes';
import { useSearch, addRecentSearch, SearchFilters, SearchProduct } from '../../hooks/useSearch';
import { useCartContext } from '../../hooks/useCart';
import { useFavoritesContext } from '../../hooks/useFavorites';
import { FASH_LOGO } from '../../constants/brand';

const SORT_OPTIONS = [
  { label: 'Relevance', value: 'relevance' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
  { label: 'Newest', value: 'newest' },
];

const PRICE_RANGES = [
  { label: 'All Prices', min: undefined, max: undefined },
  { label: 'Under $50', min: 0, max: 50 },
  { label: '$50 - $100', min: 50, max: 100 },
  { label: '$100 - $200', min: 100, max: 200 },
  { label: '$200 - $500', min: 200, max: 500 },
  { label: '$500+', min: 500, max: undefined },
];

export function SearchResultsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cart = useCartContext();
  const favorites = useFavoritesContext();
  
  const queryParam = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || '';
  const brandParam = searchParams.get('brand') || '';
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [selectedBrand, setSelectedBrand] = useState(brandParam);
  const [selectedPriceRange, setSelectedPriceRange] = useState(0); // index into PRICE_RANGES
  const [sortBy, setSortBy] = useState('relevance');
  const [localQuery, setLocalQuery] = useState(queryParam);

  // Build filters object
  const filters: SearchFilters = useMemo(() => ({
    category: selectedCategory || undefined,
    brand: selectedBrand || undefined,
    minPrice: PRICE_RANGES[selectedPriceRange].min,
    maxPrice: PRICE_RANGES[selectedPriceRange].max,
  }), [selectedCategory, selectedBrand, selectedPriceRange]);

  const { searchQuery, setSearchQuery, results, loading, error } = useSearch({
    debounceMs: 300,
    hitsPerPage: 40,
    filters,
  });

  // Sync URL params to search
  useEffect(() => {
    const q = queryParam || categoryParam || '';
    setSearchQuery(q);
    setLocalQuery(queryParam);
    if (categoryParam) setSelectedCategory(categoryParam);
    if (brandParam) setSelectedBrand(brandParam);
  }, [queryParam, categoryParam, brandParam, setSearchQuery]);

  // Sort results client-side
  const sortedResults = useMemo(() => {
    if (!results?.hits) return [];
    const sorted = [...results.hits];
    switch (sortBy) {
      case 'price_asc':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price_desc':
        return sorted.sort((a, b) => b.price - a.price);
      case 'newest':
        return sorted.reverse();
      default:
        return sorted; // relevance (Algolia default)
    }
  }, [results?.hits, sortBy]);

  const handleNewSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!localQuery.trim()) return;
    addRecentSearch(localQuery.trim());
    setSearchParams({ q: localQuery.trim() });
    setSearchQuery(localQuery.trim());
  };

  const searchOrigin = `/search?${searchParams.toString()}`;

  const handleProductClick = (id: string) => {
    navigate(`/product/${id}`, { state: { from: searchOrigin } });
  };

  const handleAddToCart = (product: SearchProduct, selectedSize?: string) => {
    const size = product.sizes?.find((item) => item.label === selectedSize && item.available);
    if (!size) {
      navigate(`/product/${product.id}`, { state: { from: searchOrigin } });
      return;
    }
    cart.addItem({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      businessId: product.brandId || '',
      price: product.price,
      size: size.label,
      quantity: 1,
      imageUrl: product.imageUrl || product.images?.[0] || '',
    });
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedBrand('');
    setSelectedPriceRange(0);
    setSortBy('relevance');
  };

  const hasActiveFilters = selectedCategory || selectedBrand || selectedPriceRange > 0;
  const activeFilterCount = [selectedCategory, selectedBrand, selectedPriceRange > 0].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <BackButton
              fallback={SHOPPER_HOME}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              iconClassName="h-5 w-5 text-heading"
            />

            {/* Search Bar */}
            <form onSubmit={handleNewSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 h-10 rounded-full border border-gray-200 focus:border-primary focus:outline-none bg-gray-50 text-sm"
              />
            </form>

            <ShopperNavActions className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" iconClassName="h-5 w-5 text-heading" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4">
        {/* Search Info Bar */}
        <div className="flex items-center justify-between mb-4">
          <div>
            {queryParam && (
              <h1 className="text-lg md:text-xl font-serif text-heading">
                Results for "{queryParam}"
              </h1>
            )}
            {!queryParam && selectedCategory && (
              <h1 className="text-lg md:text-xl font-serif text-heading">
                {selectedCategory}
              </h1>
            )}
            {results && !loading && (
              <p className="text-xs text-body mt-0.5">
                {results.totalHits} product{results.totalHits !== 1 ? 's' : ''} found
                <span className="text-gray-400 ml-1">
                  ({results.processingTimeMs.toFixed(0)}ms)
                </span>
              </p>
            )}
          </div>

          {/* Filter & Sort Toggle */}
          <div className="flex items-center gap-2">
            {/* Sort Dropdown (desktop) */}
            <div className="hidden md:flex items-center gap-2">
              <label className="text-xs text-body">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-primary"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-heading hover:bg-gray-200'
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-primary text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-heading">Filters</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-primary hover:text-primary-hover transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Category Filter */}
              <div>
                <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
                  Category
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {['All', ...(results?.facets?.categories?.map(c => c.value) || ['Dresses', 'Tops', 'Bottoms', 'Outerwear', 'Swimwear', 'Accessories'])].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat === 'All' ? '' : cat)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        (cat === 'All' && !selectedCategory) || cat === selectedCategory
                          ? 'bg-gray-900 text-white'
                          : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand Filter */}
              {results?.facets?.brands && results.facets.brands.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
                    Brand
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setSelectedBrand('')}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        !selectedBrand
                          ? 'bg-gray-900 text-white'
                          : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                      }`}
                    >
                      All
                    </button>
                    {results.facets.brands.slice(0, 8).map(({ value, count }) => (
                      <button
                        key={value}
                        onClick={() => setSelectedBrand(value === selectedBrand ? '' : value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          value === selectedBrand
                            ? 'bg-gray-900 text-white'
                            : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                        }`}
                      >
                        {value} ({count})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Filter */}
              <div>
                <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
                  Price
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {PRICE_RANGES.map((range, i) => (
                    <button
                      key={range.label}
                      onClick={() => setSelectedPriceRange(i)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        i === selectedPriceRange
                          ? 'bg-gray-900 text-white'
                          : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                      }`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Sort */}
            <div className="md:hidden mt-4 pt-3 border-t border-gray-200">
              <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
                Sort By
              </label>
              <div className="flex flex-wrap gap-1.5">
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      sortBy === opt.value
                        ? 'bg-gray-900 text-white'
                        : 'bg-white border border-gray-200 text-heading hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active Filter Pills */}
        {hasActiveFilters && !showFilters && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedCategory && (
              <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-heading">
                {selectedCategory}
                <button onClick={() => setSelectedCategory('')} className="hover:text-primary">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedBrand && (
              <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-heading">
                {selectedBrand}
                <button onClick={() => setSelectedBrand('')} className="hover:text-primary">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedPriceRange > 0 && (
              <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-heading">
                {PRICE_RANGES[selectedPriceRange].label}
                <button onClick={() => setSelectedPriceRange(0)} className="hover:text-primary">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className="text-xs text-primary hover:text-primary-hover font-medium"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-3" />
              <p className="text-sm text-body">Searching...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-20">
            <p className="text-lg font-serif text-heading mb-2">Search Error</p>
            <p className="text-sm text-body mb-4">{error}</p>
            <button
              onClick={() => setSearchQuery(queryParam)}
              className="text-sm font-medium text-primary hover:text-primary-hover"
            >
              Try again
            </button>
          </div>
        )}

        {/* No Results */}
        {!loading && !error && results && results.hits.length === 0 && (
          <div className="text-center py-20">
            <Search className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <h2 className="text-xl font-serif text-heading mb-2">No products found</h2>
            <p className="text-sm text-body mb-6 max-w-md mx-auto">
              {queryParam 
                ? `We couldn't find any products matching "${queryParam}". Try different keywords or browse our categories.`
                : 'Try searching for something or browse our categories.'
              }
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm font-medium text-primary hover:text-primary-hover mb-4 block mx-auto"
              >
                Clear all filters
              </button>
            )}
            <Link
              to={SHOPPER_HOME}
              className="inline-block text-sm font-medium text-white bg-gray-900 px-6 py-2.5 rounded-full hover:bg-gray-800 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}

        {/* Results Grid */}
        {!loading && !error && sortedResults.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-6 pb-8">
            {sortedResults.map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductClick(product.id)}
                className="cursor-pointer"
              >
                <ProductCard
                  product={{
                    id: product.id,
                    name: product.name,
                    brand: product.brand,
                    price: product.price,
                    compareAtPrice: product.compareAtPrice,
                    imageUrl: product.imageUrl || product.images?.[0] || '',
                    sizes: product.sizes,
                  }}
                  onAddToCart={(size, e) => {
                    e?.stopPropagation?.();
                    handleAddToCart(product, size);
                  }}
                  onFavorite={() => favorites.toggleFavorite({
                    productId: product.id,
                    name: product.name,
                    brand: product.brand,
                    price: product.price,
                    imageUrl: product.imageUrl || product.images?.[0] || '',
                  })}
                  isFavorited={favorites.isFavorited(product.id)}
                  compact
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
